import { useCallback, useState } from 'react'

import Paper from '@mui/material/Paper'

import StyledTable from '@components/StyledTable'
import { toFilterOptions } from '@util/filter'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { useAutosuggest } from '../../../autosuggest/hooks/useAutosuggest'
import { useProjectSelectlists } from '../../../selectlists/hooks/useProjectSelectlists'
import { useStatusConfig } from '../../../status/hooks/useStatusConfig'
import { Page } from '../../../theme/components/Page'
import { PageSection } from '../../../theme/components/PageSection'
import { useAansluitingValuelists } from '../../../valuelists/hooks/useAansluitingValuelists'
import { useValuelists } from '../../../valuelists/hooks/useValuelists'
import { ProjectOptions } from '../../project/components/options/ProjectOptions'
import type { Project as ProjectType } from '../../project/types/Project'
import { ProjectenFilters } from '../components/ProjectenFilters'
import { ProjectenTableRow } from '../components/ProjectenTableRow'
import { useProjectenSearch } from '../hooks/useProjectenSearch'

const locationHeaderCells = [
  { id: 'kpi', numeric: false, label: '', sort: false, fixed: 30 },
  { id: 'aanvraagID', numeric: true, label: 'Project id', sort: true },
  { id: 'aanvraagDatum', numeric: false, label: 'Aangevraagd', sort: true },
  {
    id: 'geplandeUitvoeringsdatum',
    numeric: false,
    label: 'Gepland',
    sort: true,
  },
  { id: 'adres', numeric: false, label: 'Adres', sort: false },
  { id: 'aantalObjecten', numeric: true, label: 'Objecten', sort: false },
  { id: 'uniqDisciplines', numeric: true, label: 'Disciplines', sort: false },
  { id: 'gebiedsCodeLabel', numeric: false, label: 'Gebied', sort: false },
  {
    id: 'statusText',
    numeric: false,
    label: 'Processtap',
    sort: false,
    fixed: 150,
  },
  { id: 'details', label: 'Details', sort: false, fixed: 150 },
] as const

const actionHeaderCells = [
  { id: 'kpi', numeric: false, label: '', sort: false },
  { id: 'aanvraagID', numeric: true, label: 'Project id', sort: true },
  { id: 'aanvraagDatum', numeric: false, label: 'Aangevraagd', sort: true },
  {
    id: 'geplandeUitvoeringsdatum',
    numeric: false,
    label: 'Gepland',
    sort: true,
  },
  { id: 'adres', numeric: false, label: 'Adres', sort: false },
  { id: 'acties', numeric: false, label: 'Acties', sort: false },
  { id: 'acties2', numeric: false, label: '', sort: false },
  { id: 'gebiedsCodeLabel', numeric: false, label: 'Gebied', sort: false },
  {
    id: 'statusText',
    numeric: false,
    label: 'Processtap',
    sort: false,
    fixed: 150,
  },
  { id: 'details', label: 'Details', sort: false, fixed: 150 },
] as const

export const Projecten = () => {
  const { data: valuelists } = useValuelists()
  const { data: aansluitingValuelists } = useAansluitingValuelists()
  const { data: statusconfig } = useStatusConfig()
  const {
    data: { items: projecten, count, skip, limit },
    isLoading,
    isError,
    initialFilters,
    onChangeFilters,
    onChangeSort,
    onChangePage,
    filters,
    sort,
    onReset,
    onRefresh,
  } = useProjectenSearch()

  const { canViewPlanningsverschil } = useAuthenticatedUser()

  const [anchorEl, setAnchorEl] = useState<HTMLElement>()
  const [selectedRow, setSelectedRow] = useState<ProjectType>()
  const { data: selectlists } = useProjectSelectlists({
    id: selectedRow?.aanvraagID || '',
  })
  const [showActions, setShowActions] = useState(false)
  const { processtappen } = statusconfig

  const [searchText, setSearchText] = useState('')
  const { data: options } = useAutosuggest({ text: searchText })

  const headerCells = showActions ? actionHeaderCells : locationHeaderCells

  const onClickMenu = useCallback((anchorEl, row) => {
    setSelectedRow(row)
    setAnchorEl(anchorEl)
  }, [])

  const onToggleShowActions = () => {
    setShowActions(!showActions)
  }

  return (
    <Page
      title={['Projecten', 'Aansluiting']}
      breadcrumbs={[
        { label: 'Aansluiting', href: '/home', tooltip: 'CAPO home' },
        { label: 'Projecten' },
      ]}
      fillScreen
    >
      <PageSection id="projecten" heading="Overzicht van uw projecten" />

      <Paper>
        <ProjectenFilters
          onChange={onChangeFilters}
          filters={filters}
          initialFilters={initialFilters}
          onSearchChange={(search) => setSearchText(search)}
          onToggleShowActions={onToggleShowActions}
          showActions={showActions}
          showPlanningsverschil={canViewPlanningsverschil}
          options={options}
          filterOptions={{
            ...valuelists,
            ...aansluitingValuelists,
            labels: toFilterOptions(aansluitingValuelists.labels),
            processtappen,
          }}
          onReset={onReset}
          onRefresh={onRefresh}
        />
        <StyledTable
          onChangeSort={onChangeSort}
          onChangePage={onChangePage}
          headerCells={headerCells}
          sort={sort}
          stats={{ count, skip, limit }}
          loading={isLoading}
          isError={isError}
        >
          {projecten.map((project) => (
            <ProjectenTableRow
              key={project.aanvraagID}
              project={project}
              onClickMenu={onClickMenu}
              showActions={showActions}
              filters={filters}
            />
          ))}
        </StyledTable>

        {selectedRow && (
          <ProjectOptions
            project={selectedRow}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            gebieden={aansluitingValuelists.gebieden}
            activiteitTemplates={valuelists.activiteitTemplates}
            selectableLabels={selectlists.labels}
          />
        )}
      </Paper>
    </Page>
  )
}
